import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

import { PageToolbarComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors, SiteState } from '@next/core-lib/site';

import {
  TableColumn,
  TableColumnTypes,
  TableConfig,
  TableFooterValueTypes,
  TableComponent,
} from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Production } from 'src/app/shared/models/production.model';

import { SummaryGroup } from '../../models/health.model';
import * as HealthActions from '../../store/actions/health.actions';
import { getHealthSummary } from './../../store/selectors/health.selectors';
import { ProductionPickerComponent } from '../../../shared/components/production-picker/production-picker.component';
import { HealthYearSummaryComponent } from '../health-year-summary/health-year-summary.component';

@Component({
  selector: 'app-health-summary',
  templateUrl: './health-summary.component.html',
  styleUrls: ['./health-summary.component.scss'],
  imports: [
    PageToolbarComponent,
    ProductionPickerComponent,
    TableComponent,
    HealthYearSummaryComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class HealthSummaryComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);

  public pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  public selectedDate: string;
  public tableConfig: TableConfig = {
    dataSource: new MatTableDataSource<SummaryGroup>(),
    pagination: false,
    selectColumn: false,
    alternateRows: true,
    activeSort: null,
    footer: true,
    actions: [],
    columns: [
      this.getColumn('name'),
      this.getColumn('numberOfAnimals', TableColumnTypes.NUMBER),
      this.getColumn('averageCount', TableColumnTypes.NUMBER),
      this.getColumn('totalHighCount'),
      this.getColumn('newMeasurementWithHighCount'),
      this.getColumn('highCountAfterCalving'),
    ],
  };
  private getHealthSummary$ = this.store.select(getHealthSummary);
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: 'health.health-summary.title' }));
    this.getHealthSummary$
      .pipe(
        filter((healthSummary) => !!healthSummary),
        takeUntil(this.destroy$),
      )
      .subscribe((healthSummary) => {
        const tableData = healthSummary.groups.map((group) => ({
          name: this.localeService.translate(`health.health-summary.group-${group.name}`),
          numberOfAnimals: group.somaticCellCount.numberOfAnimals,
          averageCount: Math.round(group.somaticCellCount.average),
          totalHighCount: `${Math.round(group.somaticCellCount.totalHighPercentage)}% (${Math.round(
            group.somaticCellCount.totalHighCount,
          )})`,
          newMeasurementWithHighCount: `${Math.round(
            group.somaticCellCount.highNewMeasurementsPercentage,
          )}% (${Math.round(group.somaticCellCount.highNewMeasurementsCount)})`,
          highCountAfterCalving: `${Math.round(
            group.somaticCellCount.highAfterCalvingPercentage,
          )}% (${Math.round(group.somaticCellCount.highAfterCalvingCount)})`,
        }));

        const footerRowData = tableData.pop();
        this.tableConfig.dataSource.data = tableData;
        this.tableConfig.columns[0].footerDescription = footerRowData.name;
        this.tableConfig.columns[1].footerDescription = `${footerRowData.numberOfAnimals}`;
        this.tableConfig.columns[2].footerDescription = `${footerRowData.averageCount}`;
        this.tableConfig.columns[3].footerDescription = footerRowData.totalHighCount;
        this.tableConfig.columns[4].footerDescription = footerRowData.newMeasurementWithHighCount;
        this.tableConfig.columns[5].footerDescription = footerRowData.highCountAfterCalving;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onProductionChanged(production: Production): void {
    this.store.dispatch(
      HealthActions.loadHealthSummaryRequest({
        date: new Date(production.date).toISOString(),
      }),
    );
    this.selectedDate = production.date;
  }

  private getColumn(
    id: string,
    type: TableColumnTypes = TableColumnTypes.STRING,
    digitsInfo?: string,
  ): TableColumn {
    return {
      id,
      label: `health.health-summary.column.${id}`,
      type,
      digitsInfo: type === TableColumnTypes.NUMBER ? digitsInfo : undefined,
      template: null,
      disableSort: true,
      headerClassName: 'flex__child--1 mat-cell-nospacing no-word-wrap',
      className: 'flex__child--1 mat-cell-nospacing',
      footerClassName: 'flex__child--1 mat-cell-nospacing footer',
      footerValueType: TableFooterValueTypes.STRING,
    };
  }
}
